import React, { useCallback, useEffect } from 'react'

import { enabledIn } from '@common'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { Modal } from '@/components/Modal'
import { useAppDispatch } from '@/utils/useAppDispatch'
import { useAppSelector } from '@/utils/useAppSelector'

import { config } from '../../../config'
import logger from '../../utils/logger'
import { Unavailable } from '../locale/Unavailable'
import { Login } from '../user/login/Login'
import { Logout } from '../user/logout/Logout'
import { closeOverlay, onOverlaySuccess, setOverlayView } from './overlaySlice'
import { ProductOverlay } from './ProductOverlay'

const FeatureFlags = dynamic<unknown>(() => import('../featureFlags/FeatureFlags').then((mod) => mod.FeatureFlags))
const { STAGE } = config

export function Overlay() {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const productId = typeof router.query.product === 'string' ? router.query.product : undefined
  const overlayView = useAppSelector((state) => state.overlay.view)
  const modalOpen = overlayView.type !== 'CLOSED'
  useEffect(() => {
    if (productId) {
      dispatch(setOverlayView({ view: { type: 'PRODUCT', productId: productId } }))
    }
  }, [productId, dispatch])

  /* Hide body scroll when modal is open */
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [modalOpen])

  /* Listen to navigations when product modal is open */
  useEffect(() => {
    router.beforePopState((nextState) => {
      const isRoot = nextState.url === '/'
      const isGamePage = nextState.url.match(/\/game\/.*/)
      if ((isRoot || isGamePage) && overlayView.type === 'PRODUCT') {
        logger.debug({ msg: 'closing overlay' })
        dispatch(closeOverlay)
      }
      return true
    })
    return () => {
      router.beforePopState(() => true)
    }
  }, [overlayView, dispatch, router])

  const onClose = useCallback(() => dispatch(closeOverlay), [dispatch])

  if (overlayView.type === 'LOGIN') {
    return (
      <Modal position="Middle" data-cy="login-modal" onClose={onClose} closeOnClickOutside>
        <Login onSuccess={() => dispatch(onOverlaySuccess)} inModal={true} />
      </Modal>
    )
  }
  if (overlayView.type === 'LOGOUT') {
    return (
      <Modal position="Middle" data-cy="logout-modal" onClose={onClose} closeOnClickOutside>
        <Logout />
      </Modal>
    )
  }
  if (overlayView.type === 'UNAVAILABLE') {
    return (
      <Modal position="Middle" data-cy="notify-modal" onClose={onClose} closeOnClickOutside>
        <Unavailable />
      </Modal>
    )
  }
  if (overlayView.type === 'PRODUCT') {
    return <ProductOverlay productId={overlayView.productId} />
  }

  if (overlayView.type === 'FEATURE_FLAGS' && enabledIn(STAGE, ['development', 'local-dev', 'staging', 'test'])) {
    return (
      <Modal position="Middle" data-cy="notify-modal" onClose={onClose} closeOnClickOutside>
        <FeatureFlags />
      </Modal>
    )
  }
  return null
}
